import {groupBy} from 'lodash'

import {
  KpiDefinitionDto,
  KpiEditDefinitionFormValues,
  KpiEditFormValues,
  KpiEditGroupFormValues,
  KpiEditTabFormValues,
  KpiGroupDefinitionPayloadDto,
  KpiTabGroupDefinitionPayloadDto,
  KpiTabValuesDto,
  SlaTargetType,
  TabTypeDto
} from '../types'

const NO_GROUP_ID = '__NO_GROUP__'

/**
 * Maps API response (KpiTabValuesDto[]) to edit form values
 * Groups KPIs by their kpiGroupId within each tab
 */
export const mapKpiTabValuesToEditFormValues = (
  tabValues: KpiTabValuesDto[]
): KpiEditFormValues => {
  const tabGroups: KpiEditTabFormValues[] = tabValues.map((tab) => {
    const groupsMap = groupBy(tab.kpiDefinitions, (def) => def.kpiGroupId ?? NO_GROUP_ID)

    const groups: KpiEditGroupFormValues[] = []
    const ungroupedDefinitions: KpiEditDefinitionFormValues[] = []

    Object.entries(groupsMap).forEach(([groupId, definitions]) => {
      const mappedDefinitions: KpiEditDefinitionFormValues[] = definitions.map(
        mapKpiDefinitionToFormValues
      )

      if (groupId === NO_GROUP_ID) {
        mappedDefinitions.forEach((def) => ungroupedDefinitions.push(def))
      } else {
        const groupInfo = tab.kpiGroups?.find((g) => g.id === groupId)
        groups.push({
          id: groupId,
          name: groupInfo?.name ?? '',
          definitions: mappedDefinitions
        })
      }
    })

    // Sort groups by name
    groups.sort((a, b) => a.name.localeCompare(b.name))
    // Sort ungrouped by name
    ungroupedDefinitions.sort((a, b) => a.name.localeCompare(b.name))

    return {
      tabType: tab.tabType,
      groups,
      ungroupedDefinitions
    }
  })

  // Ensure all tab types are present in the form values
  const existingTabTypes = new Set(tabGroups.map((t) => t.tabType))
  Object.values(TabTypeDto).forEach((tabType) => {
    if (!existingTabTypes.has(tabType)) {
      tabGroups.push({
        tabType,
        groups: [],
        ungroupedDefinitions: []
      })
    }
  })

  // Sort tabs by enum order
  const tabOrder = Object.values(TabTypeDto)
  tabGroups.sort(
    (a, b) => tabOrder.indexOf(a.tabType as TabTypeDto) - tabOrder.indexOf(b.tabType as TabTypeDto)
  )

  return {tabGroups}
}

/**
 * Maps a single KPI definition DTO to form values
 */
const mapKpiDefinitionToFormValues = (dto: KpiDefinitionDto): KpiEditDefinitionFormValues => ({
  id: dto.id,
  name: dto.name,
  kpiGroupId: dto.kpiGroupId ?? null,
  slaTarget: {
    unit: dto.slaTarget.unit,
    targetType: dto.slaTarget.targetType,
    targetValue: dto.slaTarget.targetValue,
    minValue: dto.slaTarget.minValue,
    maxValue: dto.slaTarget.maxValue
  },
  sourceKpiId: dto.cceKpiDefinitionId != null ? String(dto.cceKpiDefinitionId) : null
})

/**
 * Maps form values back to API payload format (KpiTabGroupDefinitionPayloadDto[])
 * This is used when saving the edited definitions
 */
export const mapEditFormValuesToPayload = (
  formValues: KpiEditFormValues
): KpiTabGroupDefinitionPayloadDto[] => {
  return formValues.tabGroups.map((tab) => {
    const groupDefinitions: KpiGroupDefinitionPayloadDto[] = []

    // Add grouped definitions
    tab.groups.forEach((group) => {
      groupDefinitions.push({
        group: {
          id: group.id,
          name: group.name
        },
        definitions: group.definitions.map((def) => ({
          id: def.id,
          name: def.name,
          tabType: tab.tabType,
          slaTarget: {
            unit: def.slaTarget.unit,
            targetType: def.slaTarget.targetType,
            targetValue: def.slaTarget.targetValue,
            minValue: def.slaTarget.minValue,
            maxValue: def.slaTarget.maxValue
          },
          kpiGroupId: group.id,
          cceKpiDefinitionId: def.sourceKpiId != null ? Number(def.sourceKpiId) : null
        }))
      })
    })

    // Add ungrouped definitions (group = null)
    if (tab.ungroupedDefinitions.length > 0) {
      groupDefinitions.push({
        group: null,
        definitions: tab.ungroupedDefinitions.map((def) => ({
          id: def.id,
          name: def.name,
          tabType: tab.tabType,
          slaTarget: {
            unit: def.slaTarget.unit,
            targetType: def.slaTarget.targetType,
            targetValue: def.slaTarget.targetValue,
            minValue: def.slaTarget.minValue,
            maxValue: def.slaTarget.maxValue
          },
          kpiGroupId: null,
          cceKpiDefinitionId: def.sourceKpiId != null ? Number(def.sourceKpiId) : null
        }))
      })
    }

    return {
      tabType: tab.tabType,
      groupDefinitions
    }
  })
}

/**
 * Creates a new empty KPI definition form value
 * Used when adding a new KPI (future feature)
 */
export const createEmptyKpiDefinition = (
  kpiGroupId: string | null = null
): KpiEditDefinitionFormValues => ({
  id: null,
  name: '',
  kpiGroupId,
  slaTarget: {
    unit: '',
    targetType: SlaTargetType.GreaterThan,
    targetValue: null,
    minValue: null,
    maxValue: null
  },
  sourceKpiId: null
})

/**
 * Creates a new empty group form value
 * Used when adding a new group (future feature)
 */
export const createEmptyGroup = (): KpiEditGroupFormValues => ({
  id: null,
  name: '',
  definitions: []
})

/**
 * Gets all KPI definitions from form values as a flat array
 * Useful for filtering/searching
 */
export const getAllDefinitionsFromFormValues = (
  formValues: KpiEditFormValues
): Array<{
  tabType: TabTypeDto
  groupId: string | null
  groupName: string | null
  definition: KpiEditDefinitionFormValues
}> => {
  const result: Array<{
    tabType: TabTypeDto
    groupId: string | null
    groupName: string | null
    definition: KpiEditDefinitionFormValues
  }> = []

  formValues.tabGroups.forEach((tab) => {
    // Grouped definitions
    tab.groups.forEach((group) => {
      group.definitions.forEach((def) => {
        result.push({
          tabType: tab.tabType,
          groupId: group.id,
          groupName: group.name,
          definition: def
        })
      })
    })

    // Ungrouped definitions
    tab.ungroupedDefinitions.forEach((def) => {
      result.push({
        tabType: tab.tabType,
        groupId: null,
        groupName: null,
        definition: def
      })
    })
  })

  return result
}

/**
 * Filters definitions by search term
 * Matches against definition name and group name (case-insensitive)
 */
export const filterDefinitionsBySearchTerm = (
  formValues: KpiEditFormValues,
  searchTerm: string
): Set<string> => {
  const matchingDefinitionIds = new Set<string>()
  const normalizedSearch = searchTerm.toLowerCase().trim()

  if (!normalizedSearch) {
    return matchingDefinitionIds // Empty set means show all
  }

  formValues.tabGroups.forEach((tab: KpiEditTabFormValues) => {
    tab.groups.forEach((group: KpiEditGroupFormValues) => {
      // If group name matches, include all definitions in the group
      const groupMatches = group.name.toLowerCase().includes(normalizedSearch)

      group.definitions.forEach((def: KpiEditDefinitionFormValues) => {
        if (groupMatches || def.name.toLowerCase().includes(normalizedSearch)) {
          const defId: string | null = def.id
          if (defId !== null) {
            matchingDefinitionIds.add(defId)
          }
        }
      })
    })

    // Check ungrouped definitions
    tab.ungroupedDefinitions.forEach((def: KpiEditDefinitionFormValues) => {
      if (def.name.toLowerCase().includes(normalizedSearch)) {
        const defId: string | null = def.id
        if (defId !== null) {
          matchingDefinitionIds.add(defId)
        }
      }
    })
  })

  return matchingDefinitionIds
}
